import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import {
  selectedStructureRiskAssessment,
  selectedRiskAssessmentFilters,
  isTemplateFieldDataUpdated,
  maintainSelectedEquipments,
  selectedAssemblyIds,
  unselectedAssemblyIds,
  structureTemplate as structureTemplateState,
} from '@/components/Analysis/state';
import { BreastingPoF } from './Calculations/BreastingPoF';
import { BasePoF } from './Calculations/BasePoF';
import { MooringPoF } from './Calculations/MooringPoF';
import { FenderPanelFacingPoF } from './Calculations/FenderPanelFacingPoF';
import { FenderPanelFramingPoF } from './Calculations/FenderPanelFramingPoF';
import { useRiskFactors } from '../hooks';
import { EQUATIONS, FENDER_COMPONENTS, NO_EQUATIONS_EXIST } from '../constants';
import { editorStyles, paperStyles } from './styles';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import * as riskState from '../state/atoms';
import * as analysisState from '@/components/Analysis/state';
import useDialogState from '../hooks/useDialogState';
import {
  useUpdateAssembliesProbabilityOfFailureRiskFactorsMutation,
  GetAssembliesByRiskAssessmentFiltersDocument,
  useBulkCreateCommentsForAssembliesMutation,
  GetVersionsDocument,
  CalculationType,
  RiskFieldsTypeEnum,
} from '@/__generated__/graphql';
import { abyssColors } from '@/theme/colors';
import { ActionButton, RiskTextField } from '../shared/styles';
import OpenDialogButton from '../shared/OpenDialogButton';
import { RubberFenderPoF } from './Calculations/RubberFenderPoF';
import { Tooltip } from '@mui/material';
import { useFiltersForRisk } from '../hooks/useFiltersForRisk';
import { CalculateMenu, CalculateMenuRef } from '../shared/CalculateMenu';
import { BULK_COMMENTS_FEATURE_FLAG } from '@/constants';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';

const Header = ({ selectedEquation }: { selectedEquation: CalculationType }) => {
  return (
    <Stack>
      <Typography fontWeight={500} fontSize="1.6rem" color={abyssColors.primary[500]}>
        {EQUATIONS[selectedEquation]}
      </Typography>
      <Typography fontSize="1.2rem" color={abyssColors.primary[300]}>
        POF adjusted equation
      </Typography>
      <Divider />
    </Stack>
  );
};

type ProbabilityOfFailureProps = {
  updateAllAssemblies: boolean;
  selected: boolean;
};

export const ProbabilityOfFailure = ({
  updateAllAssemblies,
  selected,
}: ProbabilityOfFailureProps) => {
  const structure = useAtomValue(selectedStructureRiskAssessment);
  const selectedFilters = useAtomValue(selectedRiskAssessmentFilters);
  const assemblyIds = useAtomValue(selectedAssemblyIds);
  const excludedAssemblyIds = useAtomValue(unselectedAssemblyIds);
  const structureTemplate = useAtomValue(structureTemplateState);
  const suppressMissingCommentsCheck = useAtomValue(riskState.suppressMissingCommentsCheck);
  const setIsTemplateFieldDataUpdated = useSetAtom(isTemplateFieldDataUpdated);
  const setEditorExpanded = useSetAtom(riskState.editorExpanded);
  const setMaintainSelections = useSetAtom(maintainSelectedEquipments);
  const setShowMissingCommentsDialog = useSetAtom(riskState.showMissingCommentsDialog);
  const setShowBulkCommentsDialog = useSetAtom(riskState.showBulkCommentsDialog);
  const [isBulkCommentsUpdateRequested, setIsBulkCommentsUpdateRequested] = useAtom(
    riskState.isBulkCommentsUpdateRequested
  );
  const [ignoreMissingComments, setIgnoreMissingComments] = useAtom(
    riskState.ignoreMissingComments
  );
  const structureInfo = useAtomValue(analysisState.structureFeatures);
  const { filters } = useFiltersForRisk({ source: 'calculations', selectedFilters });

  const calculateMenuRef = useRef<CalculateMenuRef>(null);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const bulkComments = useAtomValue(riskState.pofCommentsState);
  const [pValues, setPValues] = useState<{
    [key: string]: string;
  }>({
    risk_safety_calculated_value_pof: '',
    risk_environment_calculated_value_pof: '',
    risk_public_impact_calculated_value_pof: '',
    risk_direct_cost_calculated_value_pof: '',
    risk_business_loss_calculated_value_pof: '',
  });
  const [inputValues, setInputValues] = useState<
    {
      id: string;
      value: string;
    }[]
  >();
  const [selectedInputs, setSelectedInputs] = useState<
    { [key: string]: string | null } | undefined
  >();
  const { templateId, structureId, calculatedValues } = useRiskFactors(selectedInputs);
  const { resetDialogState, resetCommentState } = useDialogState();

  const riskFeatures: string[] = useMemo(() => {
    if (!structureInfo?.features?.riskAssessment) return [];
    return (
      (Array.isArray(structureInfo?.features?.riskAssessment) &&
        structureInfo?.features?.riskAssessment) ??
      []
    );
  }, [structureInfo?.features?.riskAssessment]);

  const calculationType: CalculationType = useMemo(() => {
    const isRubberFender = selectedFilters.some((filter) =>
      ['rubber fender', 'rubber fenders'].includes(filter.value.toLowerCase())
    );
    if (isRubberFender) return CalculationType.RubberFender;

    const isFenderPanelFacing = selectedFilters.some((filter) =>
      ['fender panel facing', 'fender facing'].includes(filter.value.toLowerCase())
    );

    if (isFenderPanelFacing) return CalculationType.FenderPanelFacing;

    const isFenderPanelFraming = selectedFilters.some((filter) =>
      [
        'fender panel framing',
        'fender panel framings',
        'fender panel chain',
        'fender panel chains',
        'fender anchor',
        'fender anchors',
        'fender framing',
        'fender weight chain',
        'fender weight chains',
        'fender shear chain',
        'fender shear chains',
      ].includes(filter.value.toLowerCase())
    );

    if (isFenderPanelFraming) return CalculationType.FenderPanelFraming;

    const isBreasting = selectedFilters.some((filter) =>
      ['breasting dolphin', 'breasting dolphins'].includes(filter.value.toLowerCase())
    );
    if (isBreasting) return CalculationType.Breasting;

    const isMooring = selectedFilters.some((filter) =>
      ['mooring dolphin', 'mooring dolphins'].includes(filter.value.toLowerCase())
    );
    if (isMooring) return CalculationType.Mooring;

    const isLoading = selectedFilters.some((filter) =>
      ['loading platform', 'loading platforms'].includes(filter.value.toLowerCase())
    );

    if (isLoading) return CalculationType.Loading;

    return CalculationType.Base;
  }, [selectedFilters]);

  const [selectedEquation, setSelectedEquation] = useState<CalculationType>(calculationType);

  useEffect(() => {
    setSelectedEquation(calculationType);
  }, [calculationType]);

  const shipsAtBerth = useMemo(() => {
    const shipsAtBerthField = structureTemplate?.fields.find(
      (f) => f.key === '(n)_number_of_ships_annually_accepted_at_the_berth'
    );
    return structure?.templateData?.find(
      (templateData) => templateData.id === shipsAtBerthField?.id
    )?.value;
  }, [structure?.templateData, structureTemplate?.fields]);

  const draftConditionLimitPercentage = useMemo(() => {
    const draftConditionLimitPercentageField = structureTemplate?.fields.find(
      (f) => f.key === 'percentage_of_time_vessel_is_at_draft_condition_governing_limits'
    );
    return structure?.templateData?.find(
      (templateData) => templateData.id === draftConditionLimitPercentageField?.id
    )?.value;
  }, [structure?.templateData, structureTemplate?.fields]);

  const hoursPerYear = useMemo(() => {
    const hoursPerYearField = structureTemplate?.fields.find(
      (f) => f.key === 'percentage_of_hours'
    );
    return structure?.templateData?.find(
      (templateData) => templateData.id === hoursPerYearField?.id
    )?.value;
  }, [structure?.templateData, structureTemplate?.fields]);

  const hoursVesselAtBerth = useMemo(() => {
    const hoursVesselAtBerthField = structureTemplate?.fields.find(
      (f) => f.key === 'number_of_hours_vessel_is_at_the_berth'
    );
    return structure?.templateData?.find(
      (templateData) => templateData.id === hoursVesselAtBerthField?.id
    )?.value;
  }, [structure?.templateData, structureTemplate?.fields]);

  const [updateAssembliesPofRiskFactors, { loading }] =
    useUpdateAssembliesProbabilityOfFailureRiskFactorsMutation({
      variables: {
        input: {
          structureId,
          templateId,
          assemblyIds,
          excludedAssemblyIds,
          selectedEquation,
          selectedInputs: calculatedValues,
          selectedFilters,
          updateAllAssemblies,
          inputValues: inputValues,
          lastModifiedFor: 'POF calculation',
        },
      },
      refetchQueries: [GetAssembliesByRiskAssessmentFiltersDocument, GetVersionsDocument],
      awaitRefetchQueries: true,
      onCompleted: () => {
        showSnackBar('The POF table has been updated successfully', 'success');
        setSelectedInputs(undefined);
        setIsTemplateFieldDataUpdated(true);
        setEditorExpanded(false);
      },
    });

  const [bulkCreateCommentsForAssemblies, { loading: loadingComments }] =
    useBulkCreateCommentsForAssembliesMutation();

  const handlePofChange = useCallback(
    ({
      pofValues,
      inputs,
    }: {
      pofValues?: { [key: string]: string };
      inputs?: {
        id: string;
        value: string;
      }[];
    }) => {
      if (pofValues) {
        setPValues(pofValues);
      }
      if (inputs) {
        setInputValues(inputs);
      }
    },
    [setPValues, setInputValues]
  );

  useEffect(() => {
    setSelectedInputs(pValues as unknown as { [key: string]: string | null } | undefined);
  }, [pValues]);

  const handleClick = useCallback(() => {
    const commentsExist =
      bulkComments?.comments &&
      bulkComments?.comments.length > 0 &&
      bulkComments.comments.some((comment) => comment.text !== '');
    if (!suppressMissingCommentsCheck && !commentsExist && !ignoreMissingComments) {
      setShowMissingCommentsDialog(true);
      return;
    }
    calculateMenuRef.current?.handleClose();
    setMaintainSelections(true);
    updateAssembliesPofRiskFactors();
    if (bulkComments?.comments && bulkComments?.comments.length > 0)
      bulkCreateCommentsForAssemblies({
        variables: {
          input: {
            structureId,
            referenceIds: assemblyIds,
            excludedAssemblyIds,
            selectedFilters,
            comments: bulkComments?.comments,
            collectionName: 'assembly',
            updateAllAssemblies,
            clearPreviousComments: true,
          },
        },
      });
    resetDialogState();
    resetCommentState();
    setIgnoreMissingComments(false);
  }, [
    bulkComments?.comments,
    suppressMissingCommentsCheck,
    ignoreMissingComments,
    setMaintainSelections,
    updateAssembliesPofRiskFactors,
    bulkCreateCommentsForAssemblies,
    structureId,
    assemblyIds,
    excludedAssemblyIds,
    selectedFilters,
    updateAllAssemblies,
    resetDialogState,
    resetCommentState,
    setIgnoreMissingComments,
    setShowMissingCommentsDialog,
  ]);

  const disableBulkComments = useMemo(() => {
    return !(
      bulkComments?.comments &&
      bulkComments?.comments.length > 0 &&
      bulkComments.comments.some((comment) => comment.text !== '')
    );
  }, [bulkComments?.comments]);

  const handleBulkComments = useCallback(() => {
    if (
      !isBulkCommentsUpdateRequested &&
      calculatedValues.length > 0 &&
      calculatedValues.some((calculated) => calculated.value !== '')
    ) {
      setShowBulkCommentsDialog(true);
      return;
    }
    setMaintainSelections(true);
    calculateMenuRef.current?.handleClose();
    if (bulkComments?.comments && bulkComments?.comments.length > 0)
      bulkCreateCommentsForAssemblies({
        variables: {
          input: {
            structureId,
            referenceIds: assemblyIds,
            excludedAssemblyIds,
            selectedFilters,
            comments: bulkComments?.comments,
            collectionName: 'assembly',
            updateAllAssemblies,
            clearPreviousComments: true,
          },
        },
        onCompleted: () => {
          showSnackBar('The comments have been updated successfully!', 'success');
          setSelectedInputs(undefined);
          setIsTemplateFieldDataUpdated(true);
          setEditorExpanded(false);
        },
      });
    setIsBulkCommentsUpdateRequested(false);
    resetDialogState();
    resetCommentState();
  }, [
    calculatedValues,
    setMaintainSelections,
    bulkComments?.comments,
    bulkCreateCommentsForAssemblies,
    structureId,
    assemblyIds,
    excludedAssemblyIds,
    selectedFilters,
    updateAllAssemblies,
    resetDialogState,
    resetCommentState,
    isBulkCommentsUpdateRequested,
    setShowBulkCommentsDialog,
    showSnackBar,
    setIsTemplateFieldDataUpdated,
    setEditorExpanded,
    setIsBulkCommentsUpdateRequested,
  ]);

  useEffect(() => {
    if (ignoreMissingComments && selected) handleClick();
  }, [handleClick, ignoreMissingComments, selected]);

  useEffect(() => {
    if (isBulkCommentsUpdateRequested && selected) handleBulkComments();
  }, [handleBulkComments, isBulkCommentsUpdateRequested, selected]);

  const equationOptions = useMemo(() => {
    if (calculationType === CalculationType.Breasting) {
      return [
        { label: EQUATIONS.MOORING, value: CalculationType.Mooring, text: 'Mooring Dolphin' },
        { label: EQUATIONS.BREASTING, value: CalculationType.Breasting, text: 'Breasting Dolphin' },
      ];
    } else if (calculationType === CalculationType.Loading) {
      return [
        { label: EQUATIONS.MOORING, value: CalculationType.Mooring, text: 'Mooring Dolphin' },
        { label: EQUATIONS.BREASTING, value: CalculationType.Breasting, text: 'Breasting Dolphin' },
        { label: EQUATIONS.LOADING, value: CalculationType.Loading, text: 'Loading Platform' },
      ];
    }
    return [];
  }, [calculationType]);

  const disableCalculation = useMemo(() => {
    const breastingDoplhinComponent = selectedFilters.some(
      (filter) =>
        filter.id === 'risk_level_2_component' &&
        filter.value.toLowerCase().includes('breasting dolphin')
    );

    const componentType = selectedFilters.some((filter) => filter.id === 'risk_component_type');

    const fenderComponentExist = filters
      .find((item) => item.key === 'risk_component_type')
      ?.options.some((option) => FENDER_COMPONENTS.includes(option.key.toLowerCase()));
    return loading || (breastingDoplhinComponent && !componentType && fenderComponentExist);
  }, [filters, loading, selectedFilters]);

  const showCalculationSelector = useMemo(
    () => [CalculationType.Breasting, CalculationType.Loading].includes(calculationType),
    [calculationType]
  );

  return (
    <Paper
      data-testid="probability-of-failure"
      elevation={0}
      sx={{ ...paperStyles, display: selected ? 'block' : 'none' }}
    >
      <Stack spacing={2} sx={editorStyles}>
        {!NO_EQUATIONS_EXIST.includes(selectedEquation) && (
          <Header selectedEquation={selectedEquation} />
        )}

        {showCalculationSelector && (
          <Stack direction="row" gap={3} pr={1.5} alignItems="flex-end" width="50%">
            <Autocomplete
              fullWidth
              value={
                selectedEquation
                  ? equationOptions.find((option) => option.value === selectedEquation)
                  : undefined
              }
              onChange={(event, newValue) =>
                setSelectedEquation(newValue ? newValue.value : CalculationType.Base)
              }
              options={equationOptions ?? []}
              getOptionLabel={(option) => option.text}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(parameters) => (
                <RiskTextField {...parameters} fullWidth label="Base Formula" variant="standard" />
              )}
            />
            <OpenDialogButton
              location={RiskFieldsTypeEnum.Pof.toString()}
              identifer="risk_selected_equation"
              title="Selected POF Equation"
            />
          </Stack>
        )}

        {selectedEquation && (
          <>
            {selectedEquation === CalculationType.Breasting && shipsAtBerth && (
              <BreastingPoF shipsAtBerth={shipsAtBerth} onChange={handlePofChange} />
            )}
            {selectedEquation === CalculationType.RubberFender && shipsAtBerth && (
              <RubberFenderPoF shipsAtBerth={shipsAtBerth} onChange={handlePofChange} />
            )}
            {selectedEquation === CalculationType.Mooring &&
              shipsAtBerth &&
              draftConditionLimitPercentage &&
              hoursPerYear &&
              hoursVesselAtBerth && (
                <MooringPoF
                  shipsAtBerth={shipsAtBerth}
                  draftConditionLimitPercentage={draftConditionLimitPercentage}
                  hoursPerYear={hoursPerYear}
                  hoursVesselAtBerth={hoursVesselAtBerth}
                  onChange={handlePofChange}
                />
              )}
            {selectedEquation === CalculationType.FenderPanelFacing && (
              <FenderPanelFacingPoF onChange={handlePofChange} />
            )}
            {selectedEquation === CalculationType.FenderPanelFraming && shipsAtBerth && (
              <FenderPanelFramingPoF shipsAtBerth={shipsAtBerth} onChange={handlePofChange} />
            )}
            {(selectedEquation === CalculationType.Base ||
              selectedEquation === CalculationType.Loading) && (
              <BasePoF onChange={handlePofChange} />
            )}
          </>
        )}
      </Stack>

      <Stack justifyContent="flex-end" mt={2} flexDirection="row" gap={2}>
        {riskFeatures.includes(BULK_COMMENTS_FEATURE_FLAG) ? (
          <>
            <Tooltip
              disableHoverListener={!disableCalculation}
              placement="bottom"
              title="Fenders have a different calculation equation. Please deselect fenders to continue"
            >
              <span>
                <CalculateMenu
                  ref={calculateMenuRef}
                  onCalculate={handleClick}
                  onUpdateComments={handleBulkComments}
                  disableComments={disableBulkComments}
                  disabled={disableCalculation}
                  loading={loading}
                  loadingComments={loadingComments}
                />
              </span>
            </Tooltip>
          </>
        ) : (
          <Tooltip
            disableHoverListener={!disableCalculation}
            placement="bottom"
            title="Fenders have a different calculation equation. Please deselect fenders to continue"
          >
            <span>
              <ActionButton disabled={disableCalculation} contained onClick={handleClick}>
                {loading ? (
                  <>
                    Calculating...
                    <CircularProgress size={20} />
                  </>
                ) : (
                  'Calculate & Update Table'
                )}
              </ActionButton>
            </span>
          </Tooltip>
        )}
      </Stack>
    </Paper>
  );
};
